import React, { useCallback, useEffect, useState } from "react";
import IndexComponent from "./Index.component";
import { getSearchHome, getRecommendList, getSearchResult } from "api/agency/v2/Search.api";
import { usePopup } from "contexts/PopupContext";
import StudentProfilePopup from "../popup/StudentProfilePopup";
import { useNavigate, useSearchParams } from "react-router-dom";

const LIST_LIMIT = 30;
const IndexContainer = () => {
    const { showPopup } = usePopup();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [hasSearch, setHasSearch] = useState<boolean>(false);

    const [isInit, setIsInit] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [page, setPage] = useState<number>(0);

    const [keyword, setKeyword] = useState<string>("");
    const [list, setList] = useState<any[]>([]);
    const [updatedProfileList, setUpdatedProfileList] = useState<any[]>([]);
    const [recentlyViewedProfileList, setRecentlyViewedProfileList] = useState<any[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);

    interface ISearchFilter {
        countryCodeList: string[];
        genderTypeCode: string;
        minAge: number;
        maxAge: number;
        minWeight: number;
        maxWeight: number;
        minHeight: number;
        maxHeight: number;
    }

    const [searchFilter, setSearchFilter] = useState<ISearchFilter>({
        countryCodeList: [],
        genderTypeCode: "",
        minAge: 0,
        maxAge: 99,
        minWeight: 0,
        maxWeight: 999,
        minHeight: 0,
        maxHeight: 999,
    });

    // URL에서 querystring 읽어서 상태 초기화 및 검색 실행
    useEffect(() => {
        const keywordFromUrl = searchParams.get('keyword') || '';
        const countryCodesFromUrl = searchParams.get('countryCodes') || '';
        const genderFromUrl = searchParams.get('gender') || '';
        const minAgeFromUrl = searchParams.get('minAge') || '0';
        const maxAgeFromUrl = searchParams.get('maxAge') || '99';
        const minWeightFromUrl = searchParams.get('minWeight') || '0';
        const maxWeightFromUrl = searchParams.get('maxWeight') || '999';
        const minHeightFromUrl = searchParams.get('minHeight') || '0';
        const maxHeightFromUrl = searchParams.get('maxHeight') || '999';

        setKeyword(keywordFromUrl);
        const newSearchFilter = {
            countryCodeList: countryCodesFromUrl ? countryCodesFromUrl.split(',') : [],
            genderTypeCode: genderFromUrl,
            minAge: parseInt(minAgeFromUrl),
            maxAge: parseInt(maxAgeFromUrl),
            minWeight: parseInt(minWeightFromUrl),
            maxWeight: parseInt(maxWeightFromUrl),
            minHeight: parseInt(minHeightFromUrl),
            maxHeight: parseInt(maxHeightFromUrl),
        };
        setSearchFilter(newSearchFilter);

        // querystring이 있으면 검색 실행 (keyword가 URL에 있으면 빈 값이어도 검색)
        if (searchParams.has('keyword') || countryCodesFromUrl || genderFromUrl) {
            setHasSearch(true);
            
            // 세션 스토리지에서 캐시된 데이터 확인
            const currentQueryString = searchParams.toString();
            const cachedData = sessionStorage.getItem('agency_search_cache');
            const cachedQueryString = sessionStorage.getItem('agency_search_query');
            
            // API 호출 함수 정의
            const executeSearchFromUrl = async () => {
                try {
                    setIsLoading(true);
                    setList([]);
                    const newSearchFilterForApi = {
                        ...newSearchFilter,
                        countryCodes: newSearchFilter.countryCodeList.join(","),
                    };
                    const data = await getSearchResult(keywordFromUrl, 1, LIST_LIMIT, newSearchFilterForApi);

                    if (data?.list) {
                        setList(data.list);
                        setPage(1);
                        setHasMore(data.list.length === LIST_LIMIT);
                        
                        // 검색 결과를 세션 스토리지에 저장
                        sessionStorage.setItem('agency_search_cache', JSON.stringify({
                            list: data.list,
                            page: 1,
                            hasMore: data.list.length === LIST_LIMIT
                        }));
                        sessionStorage.setItem('agency_search_query', currentQueryString);
                    }
                } catch (error) {
                    console.error("Failed to fetch search results:", error);
                } finally {
                    setIsLoading(false);
                    setIsInit(true);
                }
            };
            
            if (cachedData && cachedQueryString === currentQueryString) {
                // 캐시된 데이터가 있고 querystring이 동일하면 캐시 사용
                console.log('Using cached search results');
                try {
                    const parsedData = JSON.parse(cachedData);
                    setList(parsedData.list || []);
                    setPage(parsedData.page || 1);
                    setHasMore(parsedData.hasMore || false);
                    setIsInit(true);
                    
                    // 스크롤 위치 복원
                    const cachedScrollY = sessionStorage.getItem('agency_search_scroll');
                    if (cachedScrollY) {
                        setTimeout(() => {
                            window.scrollTo(0, parseInt(cachedScrollY));
                        }, 100);
                    }
                } catch (error) {
                    console.error('Failed to parse cached data:', error);
                    // 캐시 파싱 실패 시 API 호출
                    executeSearchFromUrl();
                }
            } else {
                // 캐시가 없거나 querystring이 다르면 API 호출
                executeSearchFromUrl();
            }
        } else {
            setHasSearch(false);
            setIsInit(true); // 검색하지 않을 때도 초기화 완료로 설정
            // querystring이 없으면 캐시 클리어
            sessionStorage.removeItem('agency_search_cache');
            sessionStorage.removeItem('agency_search_query');
            sessionStorage.removeItem('agency_search_scroll');
        }
    }, [searchParams]);

    const handleSearchFilter = (key: string, value: any) => {
        console.log("page/agency/v2/search/Index.container::handleSearchFilter");
        console.log({ key, value });

        if (key === "countryCodeList") {
            setSearchFilter({ ...searchFilter, countryCodeList: [...searchFilter.countryCodeList, value] });
        } else {
            setSearchFilter({ ...searchFilter, [key]: value });
        }
    }

    const handleDeleteCountryCode = (code: string) => {
        setSearchFilter({ ...searchFilter, countryCodeList: searchFilter.countryCodeList.filter((item: string) => item !== code) });
    }

    const fetchList = useCallback(async (targetPage: number) => {
        console.log("page/agency/v2/search/Index.container::fetchList");
        console.log({ targetPage });
        console.log({ isLoading });
        if (isLoading) return;

        console.log({ targetPage });
        if (targetPage === 0) return;

        console.log({ targetPage });
        try {
            setIsLoading(true);
            if (targetPage === 1) {
                setList([]);
            }
            const newSearchFilter = {
                ...searchFilter,
                countryCodes: searchFilter.countryCodeList.join(","),
            };
            const data = await getSearchResult(keyword, targetPage, LIST_LIMIT, newSearchFilter);

            if (data?.list) {
                const newList = targetPage === 1 ? data.list : [...list, ...data.list];
                setList(newList);
                setPage(targetPage);
                setHasMore(data.list.length === LIST_LIMIT);
                
                // 캐시 업데이트
                const currentQueryString = searchParams.toString();
                sessionStorage.setItem('agency_search_cache', JSON.stringify({
                    list: newList,
                    page: targetPage,
                    hasMore: data.list.length === LIST_LIMIT
                }));
                sessionStorage.setItem('agency_search_query', currentQueryString);
            }
        } catch (error) {
            console.error("Failed to fetch search results:", error);
        } finally {
            setHasSearch(true);
            setIsLoading(false);
            setIsInit(true);
        }
    }, [keyword, searchFilter, isLoading, list, searchParams]);

    const handleScroll = useCallback(() => {
        if (!hasSearch) return;

        const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
        // 스크롤이 바닥에 도달했는지 체크
        if (scrollHeight - scrollTop <= clientHeight + 100) { // 100px 여유를 둠
            console.log('scroll to bottom')
            // 더 로드할 데이터가 있고 현재 로딩 중이 아닐 때만 다음 페이지 로드
            console.log({ hasMore, isLoading });
            if (hasMore && !isLoading) {
                fetchList(page + 1);
            }
        }
    }, [hasMore, isLoading, hasSearch, page, fetchList]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const handleSearch = async (page: number) => {
        console.log("page/agency/v2/search/Index.container::handleSearch");
        console.log({ page });
        
        // querystring 생성
        const params = new URLSearchParams();
        // keyword는 빈 값이어도 항상 추가 (빈 값으로 검색하면 모든 결과 반환)
        params.set('keyword', keyword);
        if (searchFilter.countryCodeList.length > 0) {
            params.set('countryCodes', searchFilter.countryCodeList.join(','));
        }
        if (searchFilter.genderTypeCode) params.set('gender', searchFilter.genderTypeCode);
        if (searchFilter.minAge !== 0) params.set('minAge', searchFilter.minAge.toString());
        if (searchFilter.maxAge !== 99) params.set('maxAge', searchFilter.maxAge.toString());
        if (searchFilter.minWeight !== 0) params.set('minWeight', searchFilter.minWeight.toString());
        if (searchFilter.maxWeight !== 999) params.set('maxWeight', searchFilter.maxWeight.toString());
        if (searchFilter.minHeight !== 0) params.set('minHeight', searchFilter.minHeight.toString());
        if (searchFilter.maxHeight !== 999) params.set('maxHeight', searchFilter.maxHeight.toString());

        // 새로운 검색이므로 기존 캐시 클리어 (URL 변경 시 useEffect에서 새로 API 호출됨)
        sessionStorage.removeItem('agency_search_cache');
        sessionStorage.removeItem('agency_search_query');
        sessionStorage.removeItem('agency_search_scroll');

        // URL 업데이트
        navigate(`?${params.toString()}`, { replace: true });
    };

    // useEffect(() => {
    //     fetchList(1);
    // }, [fetchList]);

    const handleClickStudent = (userStudentId: number) => {
        console.log("page/agency/v2/search/Index.container::handleClickStudent");
        console.log({ userStudentId });
        
        // 현재 스크롤 위치 저장
        sessionStorage.setItem('agency_search_scroll', window.scrollY.toString());
        
        showPopup(
            <StudentProfilePopup
                userStudentId={userStudentId}
            />
        );
    }

    useEffect(() => {
        const fetchHomeInfo = async () => {
            try {
                const data = await getSearchHome();
                console.log({ data });
                if (data) {
                    if (data?.updatedProfileList) {
                        setUpdatedProfileList(data.updatedProfileList);
                    }
                    if (data?.recentlyViewedProfileList) {
                        setRecentlyViewedProfileList(data.recentlyViewedProfileList);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch recommend list:", error);
            } finally {
                setIsInit(true);
            }
        };
        fetchHomeInfo();
    }, []);

    return (
        <IndexComponent
            isInit={isInit}
            keyword={keyword}
            setKeyword={setKeyword}
            list={list}
            hasSearch={hasSearch}
            isLoading={isLoading}
            onSearch={handleSearch}
            updatedProfileList={updatedProfileList}
            recentlyViewedProfileList={recentlyViewedProfileList}
            showModal={showModal}
            setShowModal={setShowModal}
            searchFilter={searchFilter}
            onSearchFilter={handleSearchFilter}
            onDeleteCountryCode={handleDeleteCountryCode}
            onClickStudent={handleClickStudent}
        />
    );
};

export default IndexContainer;
